import type { ZmanId } from "@kedaat/engine";
import type { ZmanRow } from "./zmanim";

/** The four sky colors defined in globals.css. */
export type SkyTone = "night" | "dawn" | "day" | "dusk";

/** The moments the sky colors are pinned to. */
export interface SkyMarks {
  alos: number;
  sunrise: number;
  sunset: number;
  tzeit: number;
}

const MINUTE = 60_000;

export function skyMarks(rows: ZmanRow[]): SkyMarks | null {
  const at = (id: ZmanId) => rows.find((r) => r.zman.id === id)?.ms ?? null;
  const alos = at("alos");
  const sunrise = at("sunrise");
  const sunset = at("sunset");
  const tzeit = at("tzeit");
  if (alos === null || sunrise === null || sunset === null || tzeit === null) return null;
  if (!(alos < sunrise && sunrise < sunset && sunset < tzeit)) return null;
  return { alos, sunrise, sunset, tzeit };
}

/** Anchor points on the day, each holding one sky tone; between them the colors are mixed. */
function stops(m: SkyMarks): [number, SkyTone][] {
  const daylight = m.sunset - m.sunrise;
  return [
    [m.alos - 30 * MINUTE, "night"],
    [m.sunrise, "dawn"],
    [m.sunrise + daylight * 0.18, "day"],
    [m.sunset - daylight * 0.18, "day"],
    [m.sunset, "dusk"],
    [m.tzeit + 20 * MINUTE, "night"],
  ];
}

const tone = (name: SkyTone) => `var(--sky-${name})`;

/**
 * The color of the sky at a moment, as a CSS color. Used to tint the page and to give every
 * zman a mark in the color of its own hour, so the list reads as one day passing.
 */
export function skyColorAt(ms: number, m: SkyMarks): string {
  const points = stops(m);
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (ms <= first[0]) return tone(first[1]);
  if (ms >= last[0]) return tone(last[1]);
  for (let i = 1; i < points.length; i++) {
    const [endMs, endTone] = points[i]!;
    if (ms > endMs) continue;
    const [startMs, startTone] = points[i - 1]!;
    if (startTone === endTone) return tone(startTone);
    const share = Math.round(((endMs - ms) / (endMs - startMs)) * 100);
    if (share <= 0) return tone(endTone);
    if (share >= 100) return tone(startTone);
    return `color-mix(in oklab, ${tone(startTone)} ${share}%, ${tone(endTone)})`;
  }
  return tone(last[1]);
}
